import { Component, OnInit, OnDestroy, Input, ChangeDetectorRef, OnChanges, SimpleChanges } from '@angular/core';

import { BillingService } from '../../../services/accounting/billing.service';
import { MatterStoreService } from '../../../services/matters/matter-store.service';
import { NotyService } from '../../../services/common/noty.service';

type BillingSearchCacheEntry = {
  promise?: Promise<any>;
  response?: any;
  completedAt?: number;
};

const BILLINGS_SEARCH_CACHE_TTL_MS = 30000;

@Component({
  selector: 'app-billings-index',
  templateUrl: './billings-index.component.html',
  styleUrls: ['./billings-index.component.css'],
})
export class BillingsIndexComponent implements OnInit, OnDestroy {
  @Input() matter_id: number;

  search_options: any = {};

  componentDestroyed: boolean = false;

  count: number = 0;

  search_params: any = {
    page: 1,
    per_page: 20,
    showNonBillable: true,
    showPending: true,
    showAuthorized: true,
    showBilled: true,
  };

  billings: Array<any> = [];

  loading: boolean = true;

  loadError: string = '';

  searchOptionsLoaded: boolean = false;

  private inFlightSearchKey: string | null = null;

  private lastCompletedSearchKey: string | null = null;

  private loadedOptionsMatterId: number | null = null;

  private loadingOptionsMatterId: number | null = null;

  private static requestCache: { [key: string]: BillingSearchCacheEntry } = {};

  constructor(private billingService: BillingService, private changeDetectorRef: ChangeDetectorRef, private matterStoreService: MatterStoreService, private notyService: NotyService) {
    this.matterStoreService.billingsIndexComponent = this;
  }

  // Component lifecycle events
  ngOnInit() {}

  ngOnDestroy() {
    this.componentDestroyed = true;
    this.matterStoreService.billingsIndexComponent = null;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.matter_id && changes.matter_id.currentValue) this.loadBillingOptions();
  }

  // Component methods
  loadBillingOptions() {
    let matterId = Number(this.matter_id);
    if (this.searchOptionsLoaded && this.loadedOptionsMatterId === matterId && !this.loadError) return Promise.resolve(null);
    if (this.loadingOptionsMatterId === matterId) return Promise.resolve(null);
    this.loadingOptionsMatterId = matterId;
    this.loading = true;
    this.loadError = '';
    this.searchOptionsLoaded = false;
    this.detectChanges();
    return this.billingService
      .getSearchOptions()
      .then((response_json) => {
        this.loadingOptionsMatterId = null;
        this.loadedOptionsMatterId = matterId;
        this.search_options = response_json;
        this.search_params.per_page = response_json.per_page;
        this.searchOptionsLoaded = true;
        return this.loadBillings();
      })
      .catch(() => {
        this.loadingOptionsMatterId = null;
        this.handleLoadError('Error loading billing options');
      });
  }

  loadBillings(pageEvent: any = null, options: { force?: boolean; bypassRequestCache?: boolean } = {}) {
    let requestParams = this.buildSearchParams(pageEvent);
    Object.assign(this.search_params, requestParams);
    let searchKey: string = this.searchKey(requestParams);
    let cachedRequest = BillingsIndexComponent.requestCache[searchKey];
    if (!options.bypassRequestCache && cachedRequest) {
      if (cachedRequest.promise) {
        this.loading = true;
        this.loadError = '';
        this.detectChanges();
        return cachedRequest.promise.then((response_json) => this.applyBillingsResponse(response_json, searchKey));
      }
      if (cachedRequest.response && cachedRequest.completedAt && Date.now() - cachedRequest.completedAt < BILLINGS_SEARCH_CACHE_TTL_MS) {
        this.applyBillingsResponse(cachedRequest.response, searchKey);
        return Promise.resolve(cachedRequest.response);
      }
    }
    if (!options.force && (this.inFlightSearchKey === searchKey || this.lastCompletedSearchKey === searchKey)) return Promise.resolve(null);
    this.inFlightSearchKey = searchKey;
    this.loading = true;
    this.loadError = '';
    this.detectChanges();
    let requestPromise = this.billingService.getBillings(requestParams);
    BillingsIndexComponent.requestCache[searchKey] = { promise: requestPromise };
    return requestPromise
      .then((response_json) => {
        BillingsIndexComponent.requestCache[searchKey] = {
          response: response_json,
          completedAt: Date.now(),
        };
        this.applyBillingsResponse(response_json, searchKey);
        return response_json;
      })
      .catch(() => {
        if (BillingsIndexComponent.requestCache[searchKey] && BillingsIndexComponent.requestCache[searchKey].promise === requestPromise) delete BillingsIndexComponent.requestCache[searchKey];
        if (this.inFlightSearchKey === searchKey) this.inFlightSearchKey = null;
        this.handleLoadError('Error loading billings');
      });
  }

  filterChanged() {
    this.search_params.page = 1;
    return this.loadBillings(null, { force: true, bypassRequestCache: true });
  }

  refreshBillings() {
    return this.loadBillings(null, { force: true, bypassRequestCache: true });
  }

  pageChanged(pageEvent: any) {
    let requestParams = this.buildSearchParams(pageEvent);
    let searchKey = this.searchKey(requestParams);
    if (this.inFlightSearchKey === searchKey || this.lastCompletedSearchKey === searchKey) return Promise.resolve(null);
    return this.loadBillings(pageEvent);
  }

  selectPage(page: number, event: Event | null = null) {
    if (event) event.preventDefault();
    let nextPage = Math.max(1, Math.min(Number(page || 1), this.totalPages()));
    if (nextPage === Number(this.search_params.page)) return Promise.resolve(null);
    return this.pageChanged({ page: nextPage });
  }

  visiblePages(): Array<number> {
    let totalPages = this.totalPages();
    let currentPage = Number(this.search_params.page || 1);
    let firstPage = Math.max(1, currentPage - 2);
    let lastPage = Math.min(totalPages, firstPage + 4);
    firstPage = Math.max(1, lastPage - 4);
    let pages: Array<number> = [];
    for (let page = firstPage; page <= lastPage; page++) pages.push(page);
    return pages;
  }

  totalPages(): number {
    return Math.max(1, Math.ceil(Number(this.count || 0) / Number(this.search_params.per_page || 20)));
  }

  noPreviousPage(): boolean {
    return Number(this.search_params.page || 1) <= 1;
  }

  noNextPage(): boolean {
    return Number(this.search_params.page || 1) >= this.totalPages();
  }

  retryLoad() {
    this.loadBillingOptions();
  }

  private handleLoadError(message: string) {
    this.loading = false;
    this.loadError = message;
    this.notyService.notify(message, 'error');
    this.detectChanges();
  }

  private applyBillingsResponse(response_json: any, searchKey: string) {
    this.count = response_json.count;
    this.billings = response_json.billings;
    this.lastCompletedSearchKey = searchKey;
    if (this.inFlightSearchKey === searchKey) this.inFlightSearchKey = null;
    this.loading = false;
    this.loadError = '';
    this.detectChanges();
  }

  private detectChanges() {
    if (!this.componentDestroyed) this.changeDetectorRef.detectChanges();
  }

  private buildSearchParams(pageEvent: any = null): any {
    let requestParams = { ...this.search_params };
    requestParams.matter_id = Number(this.matter_id);
    requestParams.search_billings = true;
    if (pageEvent) requestParams.page = this.pageFromEvent(pageEvent);
    requestParams.page = Number(requestParams.page || 1);
    requestParams.per_page = Number(requestParams.per_page || 20);
    return requestParams;
  }

  private pageFromEvent(pageEvent: any): number {
    return Number(typeof pageEvent.page === 'undefined' ? pageEvent : pageEvent.page);
  }

  private searchKey(params: any = this.search_params): string {
    return JSON.stringify(params, Object.keys(params).sort());
  }
}
